import {AxisDataService} from "../types/axis-data-service.interface";
import type {FetchResult} from "../types/fetch-result.interface";
import type {ExpandResult} from "../types/expand-result.interface";
import {ValueOutsideDomainError} from "../errors/value-outside-domain.error";
import {describeValue} from "../../utils/describe-value";
import {AxisBoundaries} from "../types/axis-boundaries.interface";
import {
    InMemoryAxisDataServiceConfiguration
} from "../types/in-memory-axis-data-service.configuration.interface";

/**
 * Represents an in-memory implementation of the `AxisDataService` interface.
 * This service handles operations related to providing axis data, such as fetching values
 * and determining absoluteBoundaries, for a specified data type.
 *
 * @template TValue The type of the items managed by the service.
 */
export class InMemoryAxisDataService<TValue> implements AxisDataService<TValue> {

    private values: TValue[] | null = null;

    /**
     * Gets the identifier for the axis data service.
     *
     * @return {string} The axis data service ID.
     */
    public get axisDataServiceId(): string {
        return this._axisDataServiceId;
    }

    constructor(
        public _axisDataServiceId: string,
        private _options: InMemoryAxisDataServiceConfiguration<TValue>
    ) {
    }

    /**
     * Fetches a set of values based on the provided reference, offset, and limit.
     *
     * The offset is RELATIVE to the reference value:
     * - offset = 0: Start at reference value
     * - offset = 5: Start 5 positions AFTER reference
     * - offset = -3: Start 3 positions BEFORE reference
     *
     * @param {TValue} referenceValue - A reference value used to locate the starting point.
     * @param {number} offset - Relative offset from reference (positive = after, negative = before).
     * @param {number} limit - The maximum number of values to retrieve.
     * @return {Promise<FetchResult<TValue>>} A promise that resolves to a fetch result containing the retrieved values.
     */
    async fetchValues(referenceValue: TValue, offset: number, limit: number): Promise<FetchResult<TValue>> {
        if (!(Array.isArray(this.values))) {
            await this.initValues(referenceValue);
        }

        const values = this.values!;
        const indexOfReferenceValue = this.findValueIndex(values, referenceValue);
        if (indexOfReferenceValue === -1) {
            throw new Error(`Reference value ${referenceValue} not found in values`);
        }

        // Calculate array indices from relative offset
        const startIndex = Math.max(0, indexOfReferenceValue + offset);
        const endIndex = Math.min(startIndex + limit, values.length);

        // Calculate boundaries relative to reference value
        const min = -indexOfReferenceValue;
        const max = (values.length - 1) - indexOfReferenceValue;

        return Promise.resolve({
            values: values.slice(startIndex, endIndex),
            hasMoreBefore: startIndex > 0,
            hasMoreAfter: endIndex < values.length,
            boundaries: { min, max }
        });
    }

    /**
     * Computes and returns the absoluteBoundaries of the axis based on the given reference value.
     *
     * Boundaries are RELATIVE to the reference value (which is at index 0):
     * - min: Distance to first element (always <= 0)
     * - max: Distance to last element (always >= 0)
     *
     * Example: Array [A, B, C, D, E] with referenceValue = C (position 2)
     * - min = -2 (A is 2 positions before C)
     * - max = +2 (E is 2 positions after C)
     *
     * @param {TValue} referenceValue - The reference value (maps to index 0).
     * @return {Promise<AxisBoundaries>} A promise with min and max relative indices.
     */
    async getBoundaries(referenceValue: TValue): Promise<AxisBoundaries> {
        if (!(Array.isArray(this.values))) {
            await this.initValues(referenceValue);
        }

        const values = this.values!;
        const indexOfReferenceValue = this.findValueIndex(values, referenceValue);
        if (indexOfReferenceValue === -1) {
            throw new Error(`Reference value ${referenceValue} not found in values`);
        }

        return Promise.resolve({
            min: -indexOfReferenceValue,
            max: (values.length - 1) - indexOfReferenceValue
        });
    }

    /**
     * Expands the loaded range so it covers a value that sits outside it.
     *
     * The whole domain is already in memory after initValues(), so this is a pure slice —
     * no network, no I/O. It still returns a Promise because the interface says so.
     *
     * The returned `insertAtIndex` is RELATIVE TO THE ORIGIN, the same convention
     * fetchValues() uses for its boundaries: axis index = array position - position of the
     * reference value. That is why the reference value has to travel in; the service holds
     * no state about it, exactly like the two methods above.
     *
     * Deriving the index this way (from the arguments, never from the axis' live cache) is
     * what makes concurrent expansions safe: the provider fires many ensureValue() calls at
     * once and they all snapshot the same boundaries, so two overlapping expansions produce
     * identical index-to-value mappings instead of fighting over a moving edge.
     *
     * @param {[TValue, TValue]} loadedBoundaryValues - Currently loaded [min, max] values.
     * @param {TValue} missingValue - The value that must end up inside the loaded range.
     * @param {TValue} [referenceValue] - The axis origin; required by THIS implementation.
     * @return {Promise<ExpandResult<TValue>>} The values to insert and where to insert them.
     * @throws {ValueOutsideDomainError} If missingValue is not in this service's value domain
     *   at all. Split out from the generic case deliberately: the caller uses exactly this
     *   distinction to decide whether a dropped entry stays a local matter or is reported to
     *   monitoring, so naming it here is part of the contract rather than documentation trim.
     * @throws {Error} For every other failure — the domain is not loaded yet, the reference
     *   value is missing or unknown, a boundary value is unknown, or missingValue lies between
     *   the boundaries (a hole, which cannot occur). All of these mean something is wrong.
     */
    async expandToValue(
        loadedBoundaryValues: [TValue, TValue],
        missingValue: TValue,
        referenceValue?: TValue
    ): Promise<ExpandResult<TValue>> {
        if (!(Array.isArray(this.values))) {
            throw new Error(
                `Cannot expand axis '${this._axisDataServiceId}': no values loaded yet. ` +
                `fetchValues() or getBoundaries() must run before expandToValue().`
            );
        }

        if (referenceValue === undefined) {
            throw new Error(
                `Cannot expand axis '${this._axisDataServiceId}': no reference value given. ` +
                `This implementation needs the axis origin to return an origin-relative index.`
            );
        }

        const values = this.values;

        const referenceIndex = this.findValueIndex(values, referenceValue);
        if (referenceIndex === -1) {
            throw new Error(`Reference value ${describeValue(referenceValue)} not found in values`);
        }

        const missingIndex = this.findValueIndex(values, missingValue);
        if (missingIndex === -1) {
            // The generator's own window limit — a real, separate condition, and the EXPECTED
            // failure of this method. Typed so the caller can tell it apart from a malfunction
            // (a network-backed service failing for every value looks identical otherwise) and
            // route the two differently. Loud either way: a silent no-op would hide the drop.
            throw new ValueOutsideDomainError(
                `Value ${describeValue(missingValue)} not found in values of axis ` +
                `'${this._axisDataServiceId}' — it lies outside the generated domain.`
            );
        }

        const minIndex = this.findValueIndex(values, loadedBoundaryValues[0]);
        const maxIndex = this.findValueIndex(values, loadedBoundaryValues[1]);
        if (minIndex === -1 || maxIndex === -1) {
            throw new Error(
                `Loaded boundary value not found in values of axis '${this._axisDataServiceId}': ` +
                `[${describeValue(loadedBoundaryValues[0])}, ${describeValue(loadedBoundaryValues[1])}]`
            );
        }

        // The pair must arrive in [min, max] order. Axis passes it that way, but the interface
        // does not state the invariant, so a future caller could swap it — and swapped bounds
        // would not fail either branch below cleanly: they would yield an empty or wrong slice
        // and lose the entry silently. That is the exact failure this method exists to end.
        if (minIndex > maxIndex) {
            throw new Error(
                `Loaded boundary values are out of order for axis '${this._axisDataServiceId}': ` +
                `${describeValue(loadedBoundaryValues[0])} sorts after ${describeValue(loadedBoundaryValues[1])}. ` +
                `Expected [min, max].`
            );
        }

        // Below the window: fill from the missing value up to (not including) the current min,
        // so the inserted block ends exactly one index before it.
        if (missingIndex < minIndex) {
            return {
                values: values.slice(missingIndex, minIndex),
                insertAtIndex: missingIndex - referenceIndex,
                direction: 'before'
            };
        }

        // Above the window: fill from just after the current max up to and including the
        // missing value.
        if (missingIndex > maxIndex) {
            return {
                values: values.slice(maxIndex + 1, missingIndex + 1),
                insertAtIndex: (maxIndex + 1) - referenceIndex,
                direction: 'after'
            };
        }

        // Unreachable by design: the value cache stays continuous, so a value between the
        // boundaries is already cached and Axis.ensureValue() would have taken its fast path.
        // Say so rather than falling through and returning undefined.
        throw new Error(
            `Value ${describeValue(missingValue)} lies between the loaded boundaries of axis ` +
            `'${this._axisDataServiceId}' but is not cached. The value cache is expected to ` +
            `stay continuous — this indicates a hole in it.`
        );
    }

    /**
     * Finds the index of a value in the values array using proper comparison.
     * Handles DateTime objects (using .equals()) and primitives (using ===).
     *
     * @param values - Array of values to search in
     * @param target - The value to find
     * @returns The index of the value, or -1 if not found
     * @private
     */
    private findValueIndex(values: TValue[], target: TValue): number {
        return values.findIndex(v => this.valuesEqual(v, target));
    }

    /**
     * Compares two values for equality.
     * Handles:
     * - Primitives: strict equality (===)
     * - Objects with .equals() method (e.g., Luxon DateTime): uses .equals()
     * - Objects with .valueOf(): compares valueOf results
     *
     * @param a - First value
     * @param b - Second value
     * @returns true if values are equal
     * @protected
     */
    protected valuesEqual(a: TValue, b: TValue): boolean {
        // Strict equality for primitives and same references
        if (a === b) {
            return true;
        }

        // Handle objects with .equals() method (e.g., Luxon DateTime)
        if (a !== null && typeof a === 'object' && 'equals' in a && typeof a.equals === 'function') {
            return a.equals(b);
        }

        // Fallback: compare valueOf for objects (handles Date, etc.)
        if (a !== null && b !== null && typeof a === 'object' && typeof b === 'object') {
            return (a as object).valueOf() === (b as object).valueOf();
        }

        return false;
    }

    /**
     * Initializes the values property using the generator function from the options.
     *
     * @param referenceValue A reference value of type TValue; used for contextual initialization logic.
     * @return {void} Does not return a value.
     */
    private async initValues(referenceValue: TValue): Promise<void> {
        this.values = await this._options.generator.call(this, referenceValue);
    }
}